import AddBoxIcon from "@mui/icons-material/AddBox";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import UploadIcon from "@mui/icons-material/Upload";
import { Theme } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { PageTab } from "documents/contract-documents/_views/contracts-page-tabs";
import LoadingButton from "_shared/_views/loading/loading-button";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import {
  getApiErrorData,
  getApiErrorMessage,
  getApiErrorStatus,
} from "_shared/_errors/api-error-message";
import {
  useArchiveDocumentMutation,
  useCreateDocumentsMutation,
  useDeleteDocumentMutation,
  useGetDocumentsQuery,
  useLazyDownloadDocumentQuery,
  useUnarchiveDocumentMutation,
} from "documents/_api/document-api";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import { getDocumentUploadSnackbar, summarizeDocumentUploadResults } from "documents/_api/document-upload-results";
import Document from "documents/document";
import DocumentTopic from "documents/topics/document-topic";
import { filesize } from "filesize";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_VisibilityState as VisibilityState,
} from "material-react-table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import {
  MobileToolbarButtonsContainer,
  TableTitleRow,
  ScrollableTabs,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { useSearchParams, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";

import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { HttpStatusCode } from "axios";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import {
  getDocumentsQueryByInterface,
  updateDocumentsInterfaceQuery,
} from "documents/_stores/documents-redux-slice";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import { cleanupDeletedDocumentReferences as cleanupProposalDeletedDocumentReferences } from "work/proposals/_store/proposals-redux-slice";
import { workAgreementsApi } from "work/work-agreements/_api/work-agreement-api";
import { cleanupDeletedDocumentReferences as cleanupWorkAgreementDeletedDocumentReferences } from "work/work-agreements/_store/work-agreement-redux-slice";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import { useSelector } from "react-redux";
import { DocumentFileType } from "documents/document-file-type";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
/**
 * The weight and the size the document's name has always had. The link inside it brings its own
 * colour, its underline and its ellipsis — see `row-link`.
 */
const NameText = styled("span")(() => ({
  fontSize: "1em",
  fontWeight: "500",
  minWidth: 0,
  overflow: "hidden",
}));
const NameContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  svg: {
    width: "24px",
  },
}));
const LabelChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  // `left` is not a value `align-items` takes; it computed to `normal` and the row had no gap, so
  // the chips began where the last tab ended with nothing between them.
  alignItems: "center",
  gap: theme.spacing(1),
  // This is the tightest toolbar in the app — a long title, four long tabs, three chips, a search
  // and a create button — and below about 1600px the row is over-subscribed. Wrapping lets the
  // chips drop to a line of their own, which gives the tabs the 523px they need; without it the
  // tabs are what gives way, and at 1214px only the first of the four was on screen.
  flexWrap: "wrap",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
// The chips share the tab strip's row, and without a floor they were what gave way: `Policies`
// wrapped away from the other two *while* the tabs beside it scrolled, so the row rearranged
// itself in two directions at once. A fixed, short list of chips is the wrong thing to reflow —
// the container wraps them away as a group instead. Communications draws the same pair this way.
const FilterButtons = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  minWidth: 0,
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const UploadingLoader = styled(Loader)(() => ({
  justifyContent: "flex-start",
}));
const AlertActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const columnHelper = createMRTColumnHelper<Document | UnavailableDocument>();
type DocumentAction = "archiving" | "deleting" | "unarchiving";

function formatDocumentCount(count: number): string {
  return `${count} ${count === 1 ? "document" : "documents"}`;
}

async function runWithGuaranteedCleanup(
  action: () => Promise<unknown>,
  onSuccess: () => void,
  onError: (error: unknown) => void,
  cleanup: () => void,
): Promise<void> {
  try {
    await action();
    onSuccess();
  } catch (error) {
    onError(error);
  } finally {
    cleanup();
  }
}

export default function Contracts() {
  // Refs
  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);
  const templateUploadTabRef = React.useRef<PageTab.Conflicts | PageTab.Policies | null>(null);

  // State
  const [uploading, setUploading] = React.useState(false);
  const [documentAction, setDocumentAction] = React.useState<DocumentAction | null>(null);
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [userColumnVisibility, setUserColumnVisibility] = useState<VisibilityState>({
    type: false
  });
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Hooks
  const navigate = useNavigate();
   const session = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dispatch = useAttorneyHubDispatch();
  const [createDocuments] = useCreateDocumentsMutation();
  const [downloadDocument] = useLazyDownloadDocumentQuery();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [archiveDocument] = useArchiveDocumentMutation();
  const [unarchiveDocument] = useUnarchiveDocumentMutation();

  // Route
  const routeParams = useParams();
  const routePageTab = routeParams.pageTab as PageTab | undefined;
  const activeTab = routePageTab ?? PageTab.Templates;
  const isArchivedTab = activeTab === PageTab.Archived;
  const mine = searchParams.get("context") === "mine";
  const conflictContext = searchParams.get("context") === "conflicts";
  const policyContext = searchParams.get("context") === "policy"; 
  const clientVendor = searchParams.get("context") === "clientVendor";
  const selectionViewKey = `${activeTab}|${searchParams.toString()}|${pagination.pageIndex}|${pagination.pageSize}|${globalFilter}|${JSON.stringify(columnFilters)}|${JSON.stringify(sorting)}`;
  const previousSelectionViewKeyRef = React.useRef(selectionViewKey);

  // Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  const interfaceName = "contractsIndex";
  const documentQuery = useSelector(getDocumentsQueryByInterface(interfaceName));
  const documentsQuery = useGetDocumentsQuery(documentQuery ? { query: documentQuery } : skipToken);
  const documents = documentsQuery.data;
  const isLoading = documentsQuery.isLoading;
  const errorLoadingQuery = documentsQuery.isError;
  const showAlertBanner = Boolean(errorLoadingQuery) || uploading || documentAction !== null;
  const loadingAlertText = uploading
    ? "Uploading..."
    : documentAction === "archiving"
      ? "Archiving..."
      : documentAction === "deleting"
        ? "Deleting..."
        : "Unarchiving...";
  const showProgressBars = isLoading;
  const showSkeletons = isLoading && !documents;

  const labelsFilter = columnFilters.find((filter) => filter.id === "labels")?.value as string | undefined;

  useEffect(() => {
    if (!routePageTab) {
      navigate(`/contracts/${PageTab.Templates}`, { replace: true });
    }
  }, [navigate, routePageTab]);

  useEffect(() => {
    if (previousSelectionViewKeyRef.current === selectionViewKey) return;
    previousSelectionViewKeyRef.current = selectionViewKey;
    setRowSelection({});
  }, [selectionViewKey]);


  const defaultQuery = useMemo(() => {
    return new DocumentQuery({
      orderParameters: new DocumentOrderParameters({
        orderBy: DocumentOrderField.Name,
        orderDir: OrderDirection.Ascending,
      }),
      filterParameters: new DocumentFilterParameters({
        name: columnFilters.find((filter) => filter.id === "name")?.value as string | undefined,
        contexts: labelsFilter ? [labelsFilter] : undefined,
        isArchived: isArchivedTab,
        query: globalFilter,
      }),
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    });
  }, [columnFilters, globalFilter, isArchivedTab, labelsFilter, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (documentQuery) return;

    dispatch(
      updateDocumentsInterfaceQuery({
        interfaceName,
        query: defaultQuery,
      }),
    );
  }, [
    columnFilters,
    defaultQuery,
    dispatch,
    documentQuery,
    globalFilter,
    labelsFilter,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    let context = searchParams.get("context");
    if (context === null && routeParams.pageTab === PageTab.Policies) {
      setSearchParams((prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("context", "mine");
        return params;
      });
      context = "mine";
    }

    const contextFilters: string[] = [];
    if (!isArchivedTab && context) {
      contextFilters.push(context);
    }
    if (routeParams.pageTab === PageTab.Policies) {
      contextFilters.push("policy");
    } else if (routeParams.pageTab === PageTab.Conflicts) {
      contextFilters.push("conflict");
    } else if (routeParams.pageTab === PageTab.Templates) {
      contextFilters.push("template");
    }
    const labelsFilter = columnFilters.find((filter) => filter.id === "labels")?.value as string | undefined;
    if (labelsFilter) {
      contextFilters.push(labelsFilter);
    }
    const orderingField = sorting.find((sort) =>
      Object.values(DocumentOrderField).includes(sort.id as DocumentOrderField),
    );

    const newQuery = new DocumentQuery({
      orderParameters: new DocumentOrderParameters({
        orderBy: orderingField ? (orderingField.id as DocumentOrderField) : DocumentOrderField.Created,
        orderDir: orderingField?.desc ? OrderDirection.Descending : OrderDirection.Ascending,
      }),
      filterParameters: new DocumentFilterParameters({
        name: columnFilters.find((filter) => filter.id === "name")?.value as string | undefined,
        contexts: contextFilters,
        isArchived: isArchivedTab,
        query: globalFilter,
      }),
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    });

    if (newQuery.cacheKey === documentQuery?.cacheKey) return;

    dispatch(
      updateDocumentsInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [
    routeParams,
    searchParams,
    pagination,
    activeTab,
    isArchivedTab,
    sorting,
    globalFilter,
    columnFilters,
    session.user?.legalEntityId,
    documentQuery,
    dispatch,
    navigate,
    setSearchParams,
  ]);

  const columnVisibility = useMemo(() => ({
    ...userColumnVisibility,
    type: activeTab === PageTab.Templates && !searchParams.get("context"),
  }), [activeTab, searchParams, userColumnVisibility]);

  function getUploadContext(tab: PageTab, isTemplate: boolean): string {
    if (isTemplate) {
      return tab === PageTab.Conflicts ? "ConflictsTemplate" : "PolicyTemplate";
    }
    return tab === PageTab.Conflicts ? "Conflicts" : "Policy";
  }

  const handleUploadError = (error: unknown) => {
    const status = getApiErrorStatus(error);
    const errorMessage = getApiErrorMessage(getApiErrorData(error));
    if (status !== undefined) {
      if (status === HttpStatusCode.Forbidden) {
        enqueueSnackbar(errorMessage ?? "Unknown error occurred during upload", { variant: "error" });
        return;
      }
      if (status === HttpStatusCode.UnprocessableEntity || status === HttpStatusCode.UnsupportedMediaType) {
        enqueueSnackbar("Unsupported file type", { variant: "error" });
        return;
      }
    }
    enqueueSnackbar("Unknown error occurred during upload", {
      variant: "error",
    });
  };

  async function handleDocumentFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) return;

    const files = Array.from(event.currentTarget.files);
    if (files.length === 0) return;

    let context: string;

    if (activeTab === PageTab.Templates) {
      const templateTab = templateUploadTabRef.current;
      if (!templateTab) {
        event.target.value = "";
        return;
      }
      context = getUploadContext(templateTab, true);
    } else {
      const templateResponse = await confirm({
        title: "Create Template?",
        message: "Do you want to create a template or just upload the document?",
        okButtonText: "Create Template",
        alternativeOkText: "Just Upload",
        cancelButtonText: "Cancel",
      });

      if (templateResponse === ConfirmResponse.Cancel) {
        event.target.value = "";
        return;
      }

      const isTemplate = templateResponse === ConfirmResponse.Ok;
      context = getUploadContext(activeTab, isTemplate);
    }

    setUploading(true);
    try {
      let topic = DocumentTopic.fromJSON({ context });
      const requests = createBulkDocumentRequests(files, {
        topics: topic ? [topic] : undefined,
      });
      const results = await createDocuments({ requests }).unwrap();
      const summary = summarizeDocumentUploadResults(results, requests);
      const snackbar = getDocumentUploadSnackbar(summary);
      enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
    } catch (error) {
      handleUploadError(error);
    } finally {
      templateUploadTabRef.current = null;
      event.target.value = "";
      setUploading(false);
    }
  }

  async function handleCreateTemplateClicked(): Promise<void> {
    const templateTypeResponse = await confirm({
      title: "Create Template",
      message: "What kind of template do you want to create?",
      okButtonText: "Conflicts Template",
      alternativeOkText: "Policy Template",
      cancelButtonText: "Cancel",
    });

    if (templateTypeResponse === ConfirmResponse.Cancel) return;

    templateUploadTabRef.current =
      templateTypeResponse === ConfirmResponse.Ok ? PageTab.Conflicts : PageTab.Policies;
    documentFileUploaderRef.current?.click();
  }

  /**
   * Downloads the document file with the given id
   * @param {Guid} id The id of the document file to download
   * @returns {Promise<void>}
   */
  const handleDownload = useCallback(
    async (id: Guid): Promise<void> => {
      try {
        if (!id) {
          await Promise.reject(new Error("No document id provided"));
          return;
        }
        const response = await downloadDocument({ documentId: id.value }).unwrap();
        await downloadFile(response);
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Failed to download file", { variant: "error" });
      }
    },
    [downloadDocument],
  );

  /**
   * Deletes the document file with the given id
   * @param {Guid} id The id of the document file to delete
   * @returns {Promise<void>}
   */
  async function handleDelete(id: Guid): Promise<void> {
    if (!id) {
      await Promise.reject(new Error("No document id provided"));
      return;
    }

    const response = await confirm({
      title: "Delete Document?",
      message: "Are you sure you want to delete this document?",
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteDocument({ documentId: id.value }).unwrap();
      dispatch(cleanupProposalDeletedDocumentReferences(id));
      dispatch(cleanupWorkAgreementDeletedDocumentReferences(id));
      dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal" }]));
      dispatch(workAgreementsApi.util.invalidateTags([{ type: "WorkAgreement" }]));
      enqueueSnackbar("Document deleted", { variant: "success" });
    } catch (error) {
      if (getApiErrorStatus(error) === HttpStatusCode.Conflict) {
        await documentsQuery.refetch();
        enqueueSnackbar(
          "We could not delete this document because active work uses it. Remove the document from that work and try again.",
          { variant: "error" },
        );
      } else {
        console.error(error);
        enqueueSnackbar("Failed to delete file", { variant: "error" });
      }
    }
  }

  async function handleArchive(id: Guid): Promise<void> {
    if (!id) {
      throw new Error("No document id provided");
    }

    const response = await confirm({
      title: "Archive Document?",
      message: "Are you sure you want to archive this document? This will clear the file from this list, and you can still view archived documents on the Archived tab.",
      okButtonText: "Archive",
    });

    if (response === ConfirmResponse.Cancel) return;

    setDocumentAction("archiving");
    await runWithGuaranteedCleanup(
      () => archiveDocument({ documentId: id.value }).unwrap(),
      () => enqueueSnackbar("Document archived", { variant: "success" }),
      (error) => {
        console.error(error);
        enqueueSnackbar("Failed to archive file", { variant: "error" });
      },
      () => setDocumentAction(null),
    );
  }

  async function handleUnarchive(id: Guid): Promise<void> {
    if (!id) {
      throw new Error("No document id provided");
    }

    const response = await confirm({
      title: "Unarchive Document?",
      message: "Are you sure you want to unarchive this document? This will clear the file from this list.",
      okButtonText: "Unarchive",
    });

    if (response === ConfirmResponse.Cancel) return;

    setDocumentAction("unarchiving");
    await runWithGuaranteedCleanup(
      () => unarchiveDocument({ documentId: id.value }).unwrap(),
      () => enqueueSnackbar("Document unarchived", { variant: "success" }),
      (error) => {
        console.error(error);
        enqueueSnackbar("Failed to unarchive file", { variant: "error" });
      },
      () => setDocumentAction(null),
    );
  }

  const renderNameColumn = useCallback(
    (contract: Document | UnavailableDocument): React.ReactNode => {
      if ("issue" in contract) return <span>Unavailable Document</span>;

      const fileType = enumKeyOf(DocumentFileType, contract?.fileType?.toString()).toLocaleLowerCase();

      return (
        <NameContainer>
          <FileIcon extension={fileType} {...defaultStyles[fileType as keyof typeof defaultStyles]} />
          <NameText>
            <RowLink onClick={() => handleDownload(contract.id)}>{contract.name?.value}</RowLink>
          </NameText>
          {/*
            Beside the name rather than in a column of its own. It is empty for every document
            that finished processing, which is nearly all of them, so a column would be mostly
            whitespace - and the reader is already looking here when they wonder why a document
            they just uploaded is not answerable.
          */}
          <DocumentProcessingState status={contract.classificationStatus} />
        </NameContainer>
      );
    },
    [handleDownload]
  );

  const renderLabelsColumn = useCallback((contract: Document | UnavailableDocument) => {
    if ("issue" in contract) return null;

    const labels = [...new Set(contract?.topics?.map((topic) => topic.context) ?? [])];
    return labels.map((label) => {
      if (!label) return null;
      return <LabelChip key={label} size="small" label={label} />;
    });
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor(renderNameColumn, {
        header: "Name",
        id: "name",
        enableColumnFilter: true,
        enableSorting: true,
      }),
      columnHelper.accessor(
        (contract) => {
          if ("issue" in contract) return "N/A";
          if (contract?.topics?.some(t => t.context?.toLowerCase().includes("conflict"))) return "Conflict";
          else if (contract?.topics?.some(t => t.context?.toLowerCase().includes("policy"))) return "Policy";
          else return "Unknown"; 
        },
        {
          header: "Type",
          id: "type",
          enableColumnFilter: true,
          enableSorting: true,
          enableHiding: activeTab === PageTab.Templates && !searchParams.get("context"),
        }
      ),
      columnHelper.accessor(renderLabelsColumn, {
        header: "Labels",
        id: "labels",
        enableColumnFilter: true,
        enableSorting: true,
      }),
      columnHelper.accessor(
        (contract) => {
          if ("issue" in contract) return "N/A";
          return contract?.topics?.reduce((acc, topic) => acc + (topic.entityClass === "Work.Proposal" ? 1 : 0), 0);
        },
        {
          header: "# of Proposals",
          id: "numberOfProposals",
          enableSorting: false,
          enableColumnFilter: false,
        },
      ),
      !isArchivedTab
        ? columnHelper.accessor(
            (contract) => {
              if ("issue" in contract) return "N/A";
              return contract?.created?.format("MM/DD/YY hh:mm A");
            },
            {
              header: "Created",
              id: "created",
              enableColumnFilter: false,
            },
          )
        : null,
      columnHelper.accessor(
        (contract) => {
          if ("issue" in contract) return "N/A";
          return filesize(contract?.fileSize ?? 0);
        },
        {
          header: "Size",
          id: "fileSize",
          enableSorting: true,
          enableColumnFilter: false,
        },
      ),
    ].filter((c): c is NonNullable<typeof c> => c != null),
    [renderLabelsColumn, renderNameColumn, activeTab, searchParams, isArchivedTab]
  );

  const tableData = useMemo(() => documents?.data ?? [], [documents]);

  const getArchiveDisabledReason = useCallback((document: Document | UnavailableDocument): string | undefined => {
    if ("issue" in document) return "Document is unavailable";
    if (document.hasSystemPermissions) return "System document";
    if (document.inUse) return "Document is in use";
    return undefined;
  }, []);

  const getDeleteDisabledReason = useCallback((document: Document | UnavailableDocument): string | undefined => {
    if ("issue" in document) return "Document is unavailable";
    if (document.hasSystemPermissions) return "System document";
    if (document.inUse) return "Document is in use";
    if (!document.creatorId || !session.user?.id || !document.creatorId.isEqualTo(session.user.id)) {
      return "You are not the file owner";
    }
    return undefined;
  }, [session.user]);

  const getUnarchiveDisabledReason = useCallback(
    (document: Document | UnavailableDocument): string | undefined => {
      if ("issue" in document) return "Document is unavailable";
      if (document.hasSystemPermissions) return "System document";
      return undefined;
    },
    [],
  );

  const isBulkSelectable = useCallback(
    (document: Document | UnavailableDocument): boolean => {
      if ("issue" in document || document.hasSystemPermissions) return false;
      return isArchivedTab || !document.inUse;
    },
    [isArchivedTab],
  );

  const selectedDocuments = useMemo(
    () =>
      tableData.filter(
        (document): document is Document =>
          !("issue" in document) && Boolean(rowSelection[document.id.value]),
      ),
    [rowSelection, tableData],
  );

  const deleteDisabledReason = selectedDocuments
    .map(getDeleteDisabledReason)
    .find((reason) => reason !== undefined);
  const archiveDisabledReason = selectedDocuments
    .map(getArchiveDisabledReason)
    .find((reason) => reason !== undefined);
  const unarchiveDisabledReason = selectedDocuments
    .map(getUnarchiveDisabledReason)
    .find((reason) => reason !== undefined);
  const actionInProgressReason = documentAction ? "Another document action is in progress" : undefined;

  async function handleBulkAction(action: "archive" | "delete" | "unarchive"): Promise<void> {
    const selected = [...selectedDocuments];
    if (selected.length === 0 || documentAction) return;

    const eligibilityReason = action === "delete"
      ? deleteDisabledReason
      : action === "archive"
        ? archiveDisabledReason
        : unarchiveDisabledReason;
    if (eligibilityReason) return;

    const actionLabel = action === "unarchive" ? "Unarchive" : `${action[0].toUpperCase()}${action.slice(1)}`;
    const response = await confirm({
      title: `${actionLabel} Documents?`,
      message: `Are you sure you want to ${action} ${formatDocumentCount(selected.length)}?`,
      okButtonText: actionLabel,
    });
    if (response === ConfirmResponse.Cancel) return;

    setDocumentAction(action === "archive" ? "archiving" : action === "delete" ? "deleting" : "unarchiving");
    const results = await Promise.allSettled(
      selected.map((document) =>
        Promise.resolve().then(() => {
          if (action === "archive") return archiveDocument({ documentId: document.id.value }).unwrap();
          if (action === "unarchive") return unarchiveDocument({ documentId: document.id.value }).unwrap();
          return deleteDocument({ documentId: document.id.value }).unwrap();
        }),
      ),
    );

    const succeededDocuments = selected.filter((_document, index) => results[index].status === "fulfilled");
    const failedDocuments = selected.filter((_document, index) => results[index].status === "rejected");
    results.forEach((result) => {
      if (result.status === "rejected") console.error(result.reason);
    });

    if (action === "delete" && succeededDocuments.length > 0) {
      succeededDocuments.forEach((document) => {
        dispatch(cleanupProposalDeletedDocumentReferences(document.id));
        dispatch(cleanupWorkAgreementDeletedDocumentReferences(document.id));
      });
      dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal" }]));
      dispatch(workAgreementsApi.util.invalidateTags([{ type: "WorkAgreement" }]));
    }

    const hasVisibleUseConflict = action === "delete" && results.some(
      (result) => result.status === "rejected" && getApiErrorStatus(result.reason) === HttpStatusCode.Conflict,
    );
    if (hasVisibleUseConflict) {
      await documentsQuery.refetch();
    }

    setRowSelection(Object.fromEntries(failedDocuments.map((document) => [document.id.value, true])));
    const pastTenseAction = action === "archive" ? "archived" : action === "delete" ? "deleted" : "unarchived";
    if (hasVisibleUseConflict) {
      enqueueSnackbar(
        "We could not delete this document because active work uses it. Remove the document from that work and try again.",
        { variant: succeededDocuments.length > 0 ? "warning" : "error" },
      );
    } else if (failedDocuments.length === 0) {
      enqueueSnackbar(`${formatDocumentCount(succeededDocuments.length)} ${pastTenseAction}`, { variant: "success" });
    } else if (succeededDocuments.length === 0) {
      enqueueSnackbar(`Failed to ${action} ${formatDocumentCount(failedDocuments.length)}`, { variant: "error" });
    } else {
      enqueueSnackbar(
        `${formatDocumentCount(succeededDocuments.length)} ${pastTenseAction}; ${formatDocumentCount(failedDocuments.length)} failed`,
        { variant: "warning" },
      );
    }
    setDocumentAction(null);
  }

  const renderBulkActionButton = (
    action: "archive" | "delete" | "unarchive",
    disabledReason: string | undefined,
  ): React.ReactNode => {
    const label = action === "archive" ? "Archive" : action === "delete" ? "Delete" : "Unarchive";
    const icon = action === "archive" ? <ArchiveIcon /> : action === "delete" ? <DeleteIcon /> : <UnarchiveIcon />;
    const effectiveDisabledReason = actionInProgressReason ?? disabledReason;
    const button = isMediumDisplaySize ? (
      <IconButton
        aria-label={label}
        color={action === "delete" ? "error" : "inherit"}
        disabled={Boolean(effectiveDisabledReason)}
        onClick={() => void handleBulkAction(action)}
      >
        {icon}
      </IconButton>
    ) : (
      <Button
        color={action === "delete" ? "error" : "inherit"}
        disabled={Boolean(effectiveDisabledReason)}
        startIcon={icon}
        onClick={() => void handleBulkAction(action)}
      >
        {label}
      </Button>
    );

    return (
      <Tooltip key={action} title={effectiveDisabledReason ? `Unable to ${action}: ${effectiveDisabledReason}` : label}>
        <span>{button}</span>
      </Tooltip>
    );
  };

  const bulkActionButtons = selectedDocuments.length > 0 ? (
    <AlertActions>
      {isArchivedTab
        ? renderBulkActionButton("unarchive", unarchiveDisabledReason)
        : renderBulkActionButton("archive", archiveDisabledReason)}
      {renderBulkActionButton("delete", deleteDisabledReason)}
    </AlertActions>
  ) : undefined;

  const tableOptions: MRT_TableOptions<Document | UnavailableDocument> = {
    columns,
    data: tableData,
    enableRowSelection: (row) => documentAction === null && isBulkSelectable(row.original),
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "contracts", table }),
    // The context chips are the page's own filter and the table knows nothing about them, so
    // without `hasPageFilters` a chip that excludes everything reads as an empty account —
    // "No contracts" where the truth is "no contracts match".
    renderEmptyRowsFallback: emptyRowsFallback({
      hasPageFilters: mine || conflictContext || policyContext || clientVendor,
      isError: errorLoadingQuery,
      isLoading: documentsQuery.isFetching,
      onRetry: () => void documentsQuery.refetch(),
      subject: isArchivedTab ? "archived contracts" : "contracts",
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            {activeTab === PageTab.Templates ? (
              <LoadingButton
                loading={uploading}
                startIcon={<AddBoxIcon />}
                onClick={handleCreateTemplateClicked}
              >
                Create Template
              </LoadingButton>
            ) : !isArchivedTab ? (
              <LoadingButton
                loading={uploading}
                startIcon={<UploadIcon />}
                onClick={() => documentFileUploaderRef.current?.click()}
              >
                {`Upload ${activeTab}`}
              </LoadingButton>
            ) : null}
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitleRow>
            <TableTitle variant="h4">Contracts</TableTitle>
            {activeTab === PageTab.Templates && isMediumDisplaySize && (
              <NewButton aria-label="Create Template" color="primary" onClick={handleCreateTemplateClicked}>
                <AddBoxIcon fontSize="large" />
              </NewButton>
            )}
            {activeTab !== PageTab.Templates && !isArchivedTab && isMediumDisplaySize && (
              <NewButton aria-label="Upload" color="primary" onClick={() => documentFileUploaderRef.current?.click()}>
                <UploadIcon fontSize="large" />
              </NewButton>
            )}
          </TableTitleRow>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => navigate(`/contracts/${newValue}/`)}
            >
              <Tab value={PageTab.Templates} label="Templates" />
              <Tab value={PageTab.Conflicts} label="Conflicts" />
              <Tab value={PageTab.Policies} label="Contracts & Policies" />
              <Tab value={PageTab.Archived} label="Archived" />
            </ScrollableTabs>
            {activeTab === PageTab.Policies && (
              <FilterButtons>
                <Chip
                  icon={<FilterListIcon />}
                  label="My Policies"
                  color="primary"
                  variant={mine ? "filled" : "outlined"}
                  onClick={() => {
                    if (mine) return;
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (mine) {
                        params.set("context", "clientVendor");
                      } else {
                        params.set("context", "mine");
                      }
                      return params;
                    });
                  }}
                />
                <Chip
                  icon={<FilterListIcon />}
                  label={`My ${session.context?.viewingAsVendor ? "Clients'" : "Vendors'"} Policies`}
                  color="primary"
                  variant={clientVendor ? "filled" : "outlined"}
                  onClick={() => {
                    if (clientVendor) return;
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (clientVendor) {
                        params.set("context", "mine");
                      } else {
                        params.set("context", "clientVendor");
                      }
                      return params;
                    });
                  }}
                />
              </FilterButtons>
            )}
            {activeTab === PageTab.Templates && (
              <FilterButtons>
                <Chip
                  icon={<FilterListIcon />}
                  label="All"
                  color="primary"
                  variant={searchParams.get("context") ? "outlined" : "filled"}
                  onClick={() => {
                    setSearchParams(prevParams => {
                      const params = new URLSearchParams(prevParams);
                      params.delete("context");
                      return params;
                    })
                  }}
                />
                <Chip
                  icon={<FilterListIcon />}
                  label="Conflicts"
                  color="primary"
                  variant={conflictContext ? "filled" : "outlined"}
                  onClick={() => {
                    setSearchParams(prevParams => {
                      const params = new URLSearchParams(prevParams);
                      params.set("context", "conflicts");
                      return params;
                    });
                  }}
                />
                <Chip
                  icon={<FilterListIcon />}
                  label="Policies"
                  color="primary"
                  variant={policyContext ? "filled" : "outlined"}
                  onClick={() => {
                    setSearchParams(prevParams => {
                      const params = new URLSearchParams(prevParams);
                      params.set("context", "policy");
                      return params;
                    });
                  }}
                />
              </FilterButtons>
            )}
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    renderRowActions: ({ row }) => {
      const deleteDisabledReason = getDeleteDisabledReason(row.original);
      const archiveDisabledReason = getArchiveDisabledReason(row.original);
      const isDeleteDisabled = Boolean(deleteDisabledReason) || documentAction !== null;
      const isArchiveDisabled = Boolean(archiveDisabledReason) || documentAction !== null;
      const isUnarchiveDisabled = Boolean(getUnarchiveDisabledReason(row.original)) || documentAction !== null;
      return (
        <RowActions
          ariaLabel={`Actions for ${row.original instanceof UnavailableDocument ? "document" : row.original.name?.value ?? "document"}`}
          actions={[
            {
              key: "download",
              label: "Download",
              icon: <DownloadIcon />,
              onClick: () => handleDownload(row.original.id),
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteIcon />,
              destructive: true,
              onClick: () => handleDelete(row.original.id),
              disabled: isDeleteDisabled,
              disabledReason: deleteDisabledReason ? `Unable to delete: ${deleteDisabledReason}` : undefined,
            },
            isArchivedTab
              ? {
                  key: "unarchive",
                  label: "Unarchive",
                  icon: <UnarchiveIcon />,
                  onClick: () => handleUnarchive(row.original.id),
                  disabled: isUnarchiveDisabled,
                  disabledReason: isUnarchiveDisabled ? "Unable to unarchive" : undefined,
                }
              : {
                  key: "archive",
                  label: "Archive",
                  icon: <ArchiveIcon />,
                  onClick: () => handleArchive(row.original.id),
                  disabled: isArchiveDisabled,
                  disabledReason: archiveDisabledReason ? `Unable to archive: ${archiveDisabledReason}` : undefined,
                },
          ]}
        />
      );
    },
    getRowId: (row) => {
      if (row instanceof UnavailableDocument) return `unavailable-document-${row.id}`;
      return row.id?.value ?? "";
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: {
      action: bulkActionButtons,
      color: errorLoadingQuery ? "error" : "info",
      children: showAlertBanner
        ? errorLoadingQuery
          ? "Error loading data"
          : <UploadingLoader size={24} text={loadingAlertText} />
        : undefined,
      sx: (theme) => ({
        "& .MuiAlert-message": {
          minWidth: 0,
          width: "auto",
        },
        "& .MuiAlert-action": {
          alignItems: "center",
          alignSelf: "stretch",
          backgroundColor: "inherit",
          marginRight: 0,
          paddingRight: theme.spacing(1),
          position: "sticky",
          right: 0,
          zIndex: 1,
        },
      }),
    },
    rowCount: documents?.totalElements ?? 0,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setUserColumnVisibility,
    state: {
      columnVisibility,
      columnFilters,
      globalFilter,
      isLoading: isLoading,
      pagination,
      showAlertBanner,
      showProgressBars,
      sorting,
      rowSelection,
      showSkeletons,
    },
  };

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToContracts) return <NotAuthorized />;

  return (
    <>
      <MRTNoMemo<Document | UnavailableDocument> options={tableOptions} />
      <input type="file" hidden={true} multiple={true} ref={documentFileUploaderRef} onChange={handleDocumentFileUpload} />
    </>
  );
}
